import {type UseQueryOptions, useQuery} from '@tanstack/react-query';
import type {z} from 'zod';
import type {AppError, Result} from '../../../../../packages/utils/src';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import type {GroupedHashtagsKeyType} from '../entities/register/constants';
import {
  GetHashtagListSchema,
  LanguageMasterSchema,
} from '../entities/register/schema';

type GetHashtagList = z.infer<typeof GetHashtagListSchema>;
export type ResultGetHashtagList = Result<GetHashtagList, AppError>;

type GetHashtagListParams = {
  categoryID: number;
  version?: string;
  q?: string;
  limit?: number;
  withUsedCountBy?: string;
  group?: GroupedHashtagsKeyType;
};

export const getHashtagList = async ({
  version = DEFAULT_API_VERSION,
  q = '',
  limit = 4,
  categoryID,
  withUsedCountBy,
  group,
}: GetHashtagListParams): Promise<ResultGetHashtagList> => {
  const path = createPathWithQuery(`/${version}/service/hashtags`, {
    categoryID,
    q,
    limit,
    withUsedCountBy: withUsedCountBy || '',
    group: group || '',
  });
  const result: ResultGetHashtagList = await defaultClient.get(
    path,
    GetHashtagListSchema,
  );
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetHashtagListOptions = {
  config?: UseQueryOptions<ResultGetHashtagList>;
} & GetHashtagListParams;

export const useGetHashtagList = ({
  categoryID,
  version = DEFAULT_API_VERSION,
  q,
  limit,
  config,
  withUsedCountBy,
  group,
}: UseGetHashtagListOptions) => {
  return useQuery<ResultGetHashtagList>({
    ...config,
    queryKey: ['getHashtagList', categoryID, q, limit, withUsedCountBy, group],
    async queryFn() {
      return getHashtagList({
        categoryID,
        version,
        q,
        limit,
        withUsedCountBy,
        group,
      });
    },
  });
};

/** GET /3.0/service/master/languages */
type LanguageMaster = z.infer<typeof LanguageMasterSchema>;
export type ResultGetLanguageMaster = Result<LanguageMaster, AppError>;

type GetLanguageMaster = {
  version?: string;
  queryParams: {
    q?: string;
  };
};

export const getLanguageMaster = async ({
  version = DEFAULT_API_VERSION,
  queryParams = {
    q: '',
  },
}: GetLanguageMaster): Promise<ResultGetLanguageMaster> => {
  const path = `/${version}/service/master/languages${
    queryParams.q ? `?q=${queryParams.q}` : ''
  }`;
  const result: ResultGetLanguageMaster = await defaultClient.get(
    path,
    LanguageMasterSchema,
  );
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetLanguageMasterOptions = {
  config?: UseQueryOptions<ResultGetLanguageMaster>;
  queryParams: {
    q?: string;
  };
} & GetLanguageMaster;

export const useGetLanguageMaster = ({
  config,
  queryParams = {
    q: '',
  },
}: UseGetLanguageMasterOptions) => {
  return useQuery<ResultGetLanguageMaster>({
    ...config,
    queryKey: ['GetLanguageMaster', queryParams.q],
    async queryFn() {
      return getLanguageMaster({queryParams});
    },
  });
};
